/**
 * Where a field sits on the printed form, read from its widget title.
 *
 * USCIS titles its widgets `Pt4Line4a_FamilyName`, sometimes inside the XFA
 * path (`form1[0].#subform[2].Pt4Line4a_FamilyName[0]`). Titles that don't
 * follow the pattern (a description sentence, a snake_case key) still give
 * what they can, and never a made-up line.
 */
export type FieldLine = {
  /** "4" */
  part: string | null;
  /** "4.a" */
  line: string | null;
  /** The short label the title carries: "Family Name". */
  name: string | null;
};

const EMPTY: FieldLine = { part: null, line: null, name: null };

const PART = /^(?:[Pp]art|[Pp]t|P)\.?\s*(\d+)[\s_.-]*/;
// The sub-item letter is lowercase: in `Line5Address` the `A` starts the name.
const LINE =
  /^(?:[Ll]ine|[Ll]n|[Ii]tem)\.?\s*(\d+)(?:[_.]?([a-z]))?(?![a-z])[\s_.-]*/;

const SMALL_WORDS = new Set(
  "a an the and but or nor for so as at by in of off on per to up via vs".split(
    " "
  )
);

/** `form1[0].#subform[2].Pt4Line4a_FamilyName[0]` → `Pt4Line4a_FamilyName`. */
function widgetName(label: string): string {
  const trimmed = label.trim();
  if (!/\[\d+\]|#/.test(trimmed)) return trimmed;
  const segments = trimmed.split(".");
  return (segments[segments.length - 1] || "").replace(/\[\d+\]/g, "").trim();
}

/** `PreparerFamilyName`, `USCISOnlineAccountNumber`, `unit_ste` → Title Case words. */
export function humanizeIdentifier(identifier: string): string {
  const words = identifier
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word, i) => {
      if (/^[A-Z0-9]{2,}$/.test(word)) return word;
      const lower = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

export function parseFieldLine(pdfLabel: string | null | undefined): FieldLine {
  const raw = String(pdfLabel || "").trim();
  if (!raw) return EMPTY;
  const name = widgetName(raw);
  // A description sentence, not a widget title.
  if (!name || /\s/.test(name) || !/^[\w.#-]+$/.test(name)) return EMPTY;

  let rest = name;
  let part: string | null = null;
  let line: string | null = null;

  const partMatch = PART.exec(rest);
  // A bare `P4` with no line after it is too thin to read as a part.
  if (
    partMatch &&
    (partMatch[0].length > 2 || LINE.test(rest.slice(partMatch[0].length)))
  ) {
    part = String(Number(partMatch[1]));
    rest = rest.slice(partMatch[0].length);
  }
  const lineMatch = LINE.exec(rest);
  if (lineMatch) {
    line = `${Number(lineMatch[1])}${lineMatch[2] ? `.${lineMatch[2]}` : ""}`;
    rest = rest.slice(lineMatch[0].length);
  }

  const humanized = rest ? humanizeIdentifier(rest) : "";
  return {
    part,
    line,
    name: humanized && /[a-z]/i.test(humanized) ? humanized : null
  };
}

/** "Part 4 · Line 4.a", "Part 4", "Line 4.a", or null when the title says neither. */
export function formatFieldLine(fieldLine: FieldLine): string | null {
  const parts = [
    fieldLine.part ? `Part ${fieldLine.part}` : null,
    fieldLine.line ? `Line ${fieldLine.line}` : null
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

export function fieldLineLabel(
  pdfLabel: string | null | undefined
): string | null {
  return formatFieldLine(parseFieldLine(pdfLabel));
}

const IDENTIFIER_FIELD =
  /\b(a[\s_-]?number|alien[\s_-]?(registration[\s_-]?)?number|ssn|social[\s_-]?security|receipt|online[\s_-]?account|passport[\s_-]?(number|no)|travel[\s_-]?document[\s_-]?number|i[\s_-]?94|f?ein|tax[\s_-]?id|bar[\s_-]?number|case[\s_-]?number|(tele)?phone|fax|zip[\s_-]?code|postal[\s_-]?code)\b/i;

/** IDs and numbers the eye compares digit by digit, set in mono. */
export function isIdentifierField(
  ...names: Array<string | null | undefined>
): boolean {
  return names.some((name) => {
    const words = String(name || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ");
    return IDENTIFIER_FIELD.test(words);
  });
}
